import {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {TFunction} from 'i18next';
import {IS_LEADER_GUEST_STORAGE_KEY} from 'utils/constants';
import {PATHS} from 'Routes';
import {useSchema} from 'context/guest';
import {useReservation} from 'context/reservation';
import {SessionStorage} from '@guestapp/core';
import {useGuestGroup, usePath} from 'hooks';
import {StepInfoBox} from 'pages/Auth/components/StepInfoBox';
import {BoxInfo, NavigationStatus} from '@guestapp/ui';
import MultiRadio from 'components/common/MultiRadio';
import {Wrapper, Container, StyledButton} from './styled';
import {RadiosContent} from 'styled/common';

const getOptions = (t: TFunction) => {
  return [
    {label: t('guest_in_charge_reservation'), value: 'true'},
    {label: t('accompanying_guest'), value: 'false'},
  ];
};

function WhoIsRegistering() {
  const {t} = useTranslation();
  const [option, setOption] = useState(() => {
    const storedValue = SessionStorage.get(IS_LEADER_GUEST_STORAGE_KEY);
    const foundOption = getOptions(t).find(opt => opt.value === storedValue);
    return foundOption || getOptions(t)[0];
  });

  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const {
    identityVerificationDetails: {enabled: ivEnabled},
  } = useSchema();
  const {guestGroup} = useGuestGroup();
  const {hasAvantio} = useReservation();
  const hasNoGuests = guestGroup?.added_number_of_guests === 0;

  useEffect(() => {
    if (option.value) {
      SessionStorage.set(IS_LEADER_GUEST_STORAGE_KEY, option.value);
    }
  }, [option.value]);

  const handleConfirm = useCallback(() => {
    let nextPath = '';
    if (hasAvantio) {
      nextPath = PATHS.guestRegistration;
    }
    if (ivEnabled) {
      if (hasNoGuests) {
        nextPath = PATHS.verification.main;
      } else {
        nextPath = PATHS.guestRegistration;
      }
    } else {
      if (hasNoGuests) {
        nextPath = PATHS.addPersonalDataForm;
      } else {
        nextPath = PATHS.guestRegistration;
      }
    }
    navigate(getFullPath(nextPath));
  }, [navigate, getFullPath, hasAvantio, ivEnabled, hasNoGuests]);

  return (
    <Wrapper>
      <Container>
        <StepInfoBox
          title={t('whos_in_charge')}
          description={t('select_the_the_person_in_charge_of_this_reservation')}
        />
        <RadiosContent>
          <MultiRadio options={getOptions(t)} value={option} onChange={setOption} />
        </RadiosContent>
        <BoxInfo
          type={NavigationStatus.information}
          text={t('the_guest_in_charge_must_be_an_adult')}
        />
        <StyledButton
          primary
          label={t('confirm')}
          disabled={false}
          onClick={handleConfirm}
        />
      </Container>
    </Wrapper>
  );
}

export {WhoIsRegistering};
